import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Menu, X, User, LogOut, LayoutDashboard, ChevronDown, BookmarkPlus, HeartHandshake } from '../ui/icons';
import MixtapeLogo from '../ui/MixtapeLogo';
import Button from '../ui/Button';

// Brand book "03 · Components → Navigation/Top Nav": a light paper bar with a
// 1px taupe rule underneath, the horizontal lockup on the left and Space Mono
// links in ink on the right. The sign-up link is set bold rather than boxed —
// the same treatment Figma gives "Get Started".
const NAV_LINK = 'text-brand-ink hover:underline underline-offset-4 decoration-2 transition-colors text-sm xl:text-base';
const NAV_LINK_STRONG = 'text-brand-ink font-bold hover:underline underline-offset-4 decoration-2 text-sm xl:text-base';
const MOBILE_LINK = 'flex items-center px-4 py-3.5 text-brand-ink hover:bg-brand-rose rounded-brand-sm transition-colors text-base';
const MENU_ITEM = 'flex items-center gap-2 px-4 py-3 text-brand-ink hover:bg-brand-rose transition-colors text-label';

const Navbar = () => {
  const { user, logout, isAuthenticated, isGuest } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/'); setDropdownOpen(false); };

  const fullUser = isAuthenticated && !isGuest;
  const guest    = isAuthenticated && isGuest;

  return (
    <nav className="fixed top-0 left-0 right-0 z-40 bg-brand-cream border-b border-brand-taupe">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo — horizontal lockup, ink on paper */}
          <Link to="/" className="flex items-center" aria-label="Mixtape Memories home">
            <MixtapeLogo size={24} decorative />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-6">
            {fullUser ? (
              <>
                <Link to="/" className={NAV_LINK}>Home</Link>
                <Link to="/guide" className={NAV_LINK}>Guide</Link>
                <Link to="/articles" className={NAV_LINK}>Articles</Link>
                <Link to="/partners" className={NAV_LINK}>Partners</Link>
                <Link to="/dashboard" className={NAV_LINK}>Dashboard</Link>
                {/* Partners only, and only after signing up. Top level rather than
                    buried in the account dropdown: this is where the partner rate,
                    the comparison and the referral code live, so it is a primary
                    destination for a partner, not an account setting. */}
                {user?.accountType === 'service_provider' && (
                  <Link to="/dashboard/partner" className={NAV_LINK}>Partnership Guide</Link>
                )}
                <Link to="/contact" className={NAV_LINK}>Contact Us</Link>
                <Button as={Link} to="/projects/choose" size="sm">+ New Video</Button>
                <div className="relative">
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center gap-2 text-brand-ink transition-colors"
                    aria-expanded={dropdownOpen}
                    aria-haspopup="menu"
                  >
                    <div className="w-8 h-8 bg-brand-rose rounded-full flex items-center justify-center border border-brand-taupe">
                      {user?.avatar
                        ? <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
                        : <User className="w-4 h-4 text-brand-ink" />
                      }
                    </div>
                    <span className="text-label font-bold">{user?.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>

                  {dropdownOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-brand-paper rounded-brand-md border-2 border-brand-ink shadow-hard-sm overflow-hidden">
                      <div className="px-4 py-3 border-b border-brand-taupe/40">
                        <p className="text-brand-ink text-sm font-bold truncate">{user?.name}</p>
                        <p className="text-secondary text-label truncate">{user?.email}</p>
                      </div>
                      <Link to="/dashboard" onClick={() => setDropdownOpen(false)}
                        className={MENU_ITEM}>
                        <LayoutDashboard className="w-4 h-4" /> Dashboard
                      </Link>
                      <Link to="/profile" onClick={() => setDropdownOpen(false)}
                        className={MENU_ITEM}>
                        <User className="w-4 h-4" /> Profile
                      </Link>
                      <button onClick={handleLogout}
                        className="flex items-center gap-2 w-full px-4 py-3 text-state-error hover:bg-brand-rose transition-colors text-label">
                        <LogOut className="w-4 h-4" /> Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : guest ? (
              <>
                <Link to="/" className={NAV_LINK}>Home</Link>
                <Link to="/guide" className={NAV_LINK}>Guide</Link>
                <Link to="/articles" className={NAV_LINK}>Articles</Link>
                <Link to="/partners" className={NAV_LINK}>Partners</Link>
                <Link to="/contact" className={NAV_LINK}>Contact Us</Link>
                <Button as={Link} to="/projects/choose" size="sm">+ New Video</Button>
                <Link to="/login" className={NAV_LINK}>Sign In</Link>
                <Button as={Link} to="/register" title="Create a free account to save your video for 10 days"
                  variant="secondary" size="sm" icon={BookmarkPlus} iconPosition="left">
                  Save for 10 days
                </Button>
              </>
            ) : (
              <>
                <Link to="/" className={NAV_LINK}>Home</Link>
                <Link to="/guide" className={NAV_LINK}>Guide</Link>
                <Link to="/articles" className={NAV_LINK}>Articles</Link>
                <Link to="/partners" className={NAV_LINK}>Partners</Link>
                <Link to="/contact" className={NAV_LINK}>Contact Us</Link>
                <Link to="/login" className={NAV_LINK}>Sign In</Link>
                {/* "Sign up free" rather than "Get Started": registration costs
                    nothing on every route this button appears on, and naming the
                    price is what makes the click feel safe. */}
                <Link to="/register" className={NAV_LINK_STRONG}>Sign up free</Link>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-brand-ink p-2 -mr-2"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-brand-cream border-t border-brand-taupe/40 px-4 py-3 space-y-1">
          {fullUser ? (
            <>
              <Link to="/" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Home</Link>
              <Link to="/guide" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Guide</Link>
              <Link to="/articles" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Articles</Link>
              <Link to="/partners" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Partners</Link>
              <Link to="/dashboard" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Dashboard</Link>
              {user?.accountType === 'service_provider' && (
                <Link to="/dashboard/partner" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>
                  <HeartHandshake className="w-4 h-4 mr-2" /> Partnership Guide
                </Link>
              )}
              <Link to="/projects/choose" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>New Video</Link>
              <Link to="/contact" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Contact Us</Link>
              <Link to="/profile" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Profile</Link>
              <button onClick={handleLogout} className="flex items-center w-full px-4 py-3 text-state-error hover:bg-brand-rose rounded-brand-sm transition-colors text-base">Sign Out</button>
            </>
          ) : guest ? (
            <>
              <Link to="/" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Home</Link>
              <Link to="/guide" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Guide</Link>
              <Link to="/articles" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Articles</Link>
              <Link to="/partners" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Partners</Link>
              <Link to="/projects/choose" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>+ New Video</Link>
              <Link to="/contact" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Contact Us</Link>
              <Link to="/login" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Sign In</Link>
              <Link to="/register" onClick={() => setMobileOpen(false)} className={`${MOBILE_LINK} font-bold`}>Save for 10 days</Link>
            </>
          ) : (
            <>
              <Link to="/" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Home</Link>
              <Link to="/guide" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Guide</Link>
              <Link to="/articles" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Articles</Link>
              <Link to="/partners" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Partners</Link>
              <Link to="/contact" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Contact Us</Link>
              <Link to="/login" onClick={() => setMobileOpen(false)} className={MOBILE_LINK}>Sign In</Link>
              <Link to="/register" onClick={() => setMobileOpen(false)} className={`${MOBILE_LINK} font-bold`}>Sign up free</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
